import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  CREATE_REPORT,
  DELETE_REPORT,
  GET_ALL_REPORTS,
  GET_RECONCILATION_AMOUNT,
  GET_REPORT_DOWNLOAD_STATUS,
  GET_REPORT_DOWNLOAD_URL,
  INVOKE_LAMBDA_FUNCTION,
  PREVIEW_RECONCILIATION,
  PROCESS_RECONCILIATION,
  GET_HTS_CODES_FOR_DROPDOWN,
  GET_COUNTRIES_FOR_DROPDOWN,
  GET_COMMERCIAL_DESCRIPTIONS_FOR_DROPDOWN,
  GET_MANUFACTURERS_FOR_DROPDOWN,
  CREATE_TRADE_DATA_EXPORT,
} from "@/graphql";

import {
  ReconciliationInput,
  ReconciliationPreviewResponse,
  ReconciliationProcessResponse,
  ReconciliationFiltersInput,
  DropdownOption,
  HTSCodeOption,
  HTSCodesResponse,
  ManufacturersResponse,
} from "@/types/reconciliation";

interface ILambdaReponse {
  progressBar?: boolean;
  signedUrl?: string;
  fileId?: string;
}

export const reportsApi = createApi({
  reducerPath: "api/reports",
  tagTypes: ["Reports"],
  baseQuery,
  endpoints: (builder) => ({
    getAllReports: builder.query<
      ReportsReponse["getAllReports"],
      {
        reportType?: "POST_IMPORT_ADJUSTMENTS" | "PRE_IMPORT_SERVICES";
        adjustmentType?: "value_adjustment" | "duty_rate_adjustment" | "combined_value_and_duty";
        pageNo?: number;
        pageSize?: number;
        startDate?: string;
        endDate?: string;
        clientId?: string;
        tenantId?: string;
        onlyAdjustments?: boolean;
        onlyExports?: boolean;
      }
    >({
      query: (variables) => ({
        document: GET_ALL_REPORTS,
        variables: { ...variables },
      }),
      providesTags: ["Reports"],
      transformResponse: (response: ReportsReponse) => response.getAllReports,
    }),
    getTotalReconcilation: builder.query<
      number,
      {
        currency: Currency;
        clientId?: string;
        tenantId?: string;
        dateTo?: number; // Date in 32bit int
        dateFrom?: number; // Date in 32bit int
        filters?: ReconciliationFiltersInput;
      }
    >({
      query: (variables) => ({
        document: GET_RECONCILATION_AMOUNT,
        variables: { ...variables },
      }),
      transformResponse: (response: ReconcilationReponse) => {
        return response.getTotalSumReconciliation.totalAmount;
      },
    }),
    createReports: builder.mutation<
      CreateReportsReponse["createReport"],
      {
        clientId?: string;
        tenantId?: string;
        report: {
          isCompleted?: boolean;
          reportType?: "POST_IMPORT_ADJUSTMENTS" | "PRE_IMPORT_SERVICES";
          result?: number;
          selectedOptions?: string;
          stepsCompleted?: number;
        };
      }
    >({
      query: (variables) => ({
        document: CREATE_REPORT,
        variables: { ...variables },
      }),
      invalidatesTags: ["Reports"],
      transformResponse: (response: CreateReportsReponse) =>
        response.createReport,
    }),
    deleteReports: builder.mutation<
      DeleteReportsReponse["deleteReport"],
      string
    >({
      query: (id) => ({
        document: DELETE_REPORT,
        variables: { id },
      }),
      invalidatesTags: ["Reports"],
      transformResponse: (response: DeleteReportsReponse) =>
        response.deleteReport,
    }),
    getFileId: builder.mutation<ILambdaReponse, GetReportFileIdDto>({
      query: (inputs) => ({
        document: INVOKE_LAMBDA_FUNCTION,
        variables: { ...inputs },
      }),
      transformResponse: (response: { invokeLambdaFunction: ILambdaReponse }) =>
        response.invokeLambdaFunction,
    }),
    getDownloadStatus: builder.query<
      ReportDownloadStatusReponse["getDownload"],
      string
    >({
      query: (id) => ({
        document: GET_REPORT_DOWNLOAD_STATUS,
        variables: {
          input: {
            fileId: id,
          },
        },
      }),
      transformResponse: (response: ReportDownloadStatusReponse) =>
        response.getDownload,
    }),
    previewReconciliation: builder.mutation<
      ReconciliationPreviewResponse,
      ReconciliationInput
    >({
      query: (input) => ({
        document: PREVIEW_RECONCILIATION,
        variables: { input },
      }),
      transformResponse: (response: { previewReconciliation: ReconciliationPreviewResponse }) =>
        response.previewReconciliation,
    }),
    processReconciliation: builder.mutation<
      ReconciliationProcessResponse,
      ReconciliationInput
    >({
      query: (input) => ({
        document: PROCESS_RECONCILIATION,
        variables: { input },
      }),
      transformResponse: (response: { processReconciliation: ReconciliationProcessResponse }) =>
        response.processReconciliation,
    }),
    getHTSCodesForDropdown: builder.query<
      HTSCodeOption[],
      { clientId?: string; tenantId?: string; includeChapter99?: boolean }
    >({
      query: (variables) => ({
        document: GET_HTS_CODES_FOR_DROPDOWN,
        variables,
      }),
      transformResponse: (response: HTSCodesResponse) => {
        return response.getHTSCodesForDropdown.data;
      },
    }),
    getCountriesForDropdown: builder.query<
      DropdownOption[],
      { clientId?: string; dateFrom?: string; dateTo?: string }
    >({
      query: (variables) => ({
        document: GET_COUNTRIES_FOR_DROPDOWN,
        variables,
      }),
      transformResponse: (response: { getCountryOriginSummary: Array<{ country_code: string; total_duties: number; total_goods_value: number }> }) => {
        return response.getCountryOriginSummary.map(item => ({
          value: item.country_code,
          label: item.country_code,
        }));
      },
    }),
    getCommercialDescriptionsForDropdown: builder.query<
      DropdownOption[],
      { clientId?: string; tenantId?: string; dateFrom?: string; dateTo?: string }
    >({
      query: (variables) => ({
        document: GET_COMMERCIAL_DESCRIPTIONS_FOR_DROPDOWN,
        variables,
      }),
      transformResponse: (response: {
        getCommercialDescriptionsForDropdown: { data: Array<{ value: string; label: string }> }
      }) => {
        return response.getCommercialDescriptionsForDropdown.data;
      },
    }),
    getManufacturersForDropdown: builder.query<
      DropdownOption[],
      { dashboardVisibility: any }
    >({
      query: (variables) => ({
        document: GET_MANUFACTURERS_FOR_DROPDOWN,
        variables,
      }),
      transformResponse: (response: ManufacturersResponse) => {
        return response.dashboardVisibilityGetManufacturers.data.map(item => ({
          value: item.manufacturer,
          label: item.manufacturerName || item.manufacturer,
          meta: {
            tariffValue: item.tariffValue,
            tariffDuty: item.tariffDuty,
            customValue: item.customValue,
          },
        }));
      },
    }),
    getReportDownloadUrl: builder.query<
      string,
      { reportId: string }
    >({
      query: (variables) => ({
        document: GET_REPORT_DOWNLOAD_URL,
        variables: { reportId: variables.reportId },
      }),
      transformResponse: (response: { getReportDownloadUrl: { data: string } }) => {
        return response.getReportDownloadUrl.data;
      },
    }),
    createTradeDataExport: builder.mutation<{ data: string }, { dateFrom: string; dateTo: string; clientId?: string; tenantId?: string }>({
      query: (input) => ({
        document: CREATE_TRADE_DATA_EXPORT,
        variables: { input },
      }),
      invalidatesTags: ["Reports"],
      transformResponse: (response: { createTradeDataExport: { data: string } }) => response.createTradeDataExport,
    }),
  }),
});

export const {
  useGetAllReportsQuery,
  useGetTotalReconcilationQuery,
  useCreateReportsMutation,
  useDeleteReportsMutation,
  useGetFileIdMutation,
  useLazyGetDownloadStatusQuery,
  usePreviewReconciliationMutation,
  useProcessReconciliationMutation,
  useGetHTSCodesForDropdownQuery,
  useGetCountriesForDropdownQuery,
  useGetCommercialDescriptionsForDropdownQuery,
  useGetManufacturersForDropdownQuery,
  useLazyGetReportDownloadUrlQuery,
  useCreateTradeDataExportMutation,
} = reportsApi;
